import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, PhoneOff, Mic, MicOff, Volume2, Loader, X, Settings, Send, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { api } from '../lib/auth';
import { toast } from 'sonner';

// Voice selection for Athena
const ATHENA_VOICES = [
  { id: "EXAVITQu4vr4xnSDxMaL", name: "Sarah", description: "Soft & warm" },
  { id: "21m00Tcm4TlvDq8ikWAM", name: "Rachel", description: "Calm & professional" },
  { id: "AZnzlk1XvdvUeBnXmlld", name: "Domi", description: "Strong & confident" },
  { id: "MF3mGyEYCl7XYWbV9V6O", name: "Elli", description: "Young & friendly" },
  { id: "jBpfuIE2acCO8z3wKNLl", name: "Gigi", description: "Expressive" },
];

// Detect if mobile
const isMobile = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

export const AthenaVoiceChat = ({ onClose, isOpen }) => {
  const [isCallActive, setIsCallActive] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [athenaResponse, setAthenaResponse] = useState('');
  const [conversationHistory, setConversationHistory] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState(ATHENA_VOICES[0].id);
  const [showVoiceSettings, setShowVoiceSettings] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [recognitionSupported, setRecognitionSupported] = useState(true);
  const [textInput, setTextInput] = useState('');
  const [useTextMode, setUseTextMode] = useState(false);
  const [isMobileDevice] = useState(isMobile());
  
  const recognitionRef = useRef(null);
  const audioRef = useRef(null);
  const callTimerRef = useRef(null);
  const isListeningRef = useRef(false);
  
  // Initialize speech recognition
  useEffect(() => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      setRecognitionSupported(false);
      setUseTextMode(true);
      console.log('Speech recognition not supported');
      return;
    }
    
    // On mobile, default to text mode but allow voice
    if (isMobileDevice) {
      setUseTextMode(true);
    }
    
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    recognitionRef.current = new SpeechRecognition();
    recognitionRef.current.continuous = false;
    recognitionRef.current.interimResults = true;
    recognitionRef.current.lang = 'en-US';
    recognitionRef.current.maxAlternatives = 1;
    
    recognitionRef.current.onstart = () => {
      console.log('Speech recognition started');
      isListeningRef.current = true;
      setIsListening(true);
    };
    
    recognitionRef.current.onresult = (event) => {
      let finalTranscript = '';
      let interimTranscript = '';
      
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          finalTranscript += result[0].transcript;
        } else {
          interimTranscript += result[0].transcript;
        }
      }
      
      if (interimTranscript) {
        setTranscript(interimTranscript);
      }
      
      if (finalTranscript && finalTranscript.trim()) {
        console.log('Final transcript:', finalTranscript);
        setTranscript(finalTranscript);
        stopListening();
        sendMessageToAthena(finalTranscript);
      }
    };
    
    recognitionRef.current.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      isListeningRef.current = false;
      setIsListening(false);
      
      if (event.error === 'not-allowed') {
        toast.error('Microphone access denied. You can use text input instead.');
        setUseTextMode(true);
      } else if (event.error === 'no-speech') {
        if (isCallActive && !isSpeaking && !isProcessing && !isMuted && !useTextMode) {
          setTimeout(() => startListening(), 500);
        }
      }
    };
    
    recognitionRef.current.onend = () => {
      console.log('Speech recognition ended');
      isListeningRef.current = false;
      setIsListening(false);
    };
    
    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) {}
      }
    };
  }, []);
  
  // Call duration timer
  useEffect(() => {
    if (isCallActive) {
      callTimerRef.current = setInterval(() => {
        setCallDuration(prev => prev + 1);
      }, 1000);
    } else {
      if (callTimerRef.current) {
        clearInterval(callTimerRef.current);
      }
      setCallDuration(0);
    }
    
    return () => {
      if (callTimerRef.current) {
        clearInterval(callTimerRef.current);
      }
    };
  }, [isCallActive]);
  
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  
  const startListening = useCallback(() => {
    if (!recognitionRef.current || isListeningRef.current || isSpeaking || isProcessing || isMuted) {
      return;
    }
    
    try {
      console.log('Starting speech recognition...');
      recognitionRef.current.start();
    } catch (e) {
      console.error('Failed to start recognition:', e);
      setTimeout(() => {
        try {
          recognitionRef.current.start();
        } catch (e2) {
          console.error('Retry failed:', e2);
          toast.error('Could not start microphone. Please use text input.');
        }
      }, 300);
    }
  }, [isSpeaking, isProcessing, isMuted]);
  
  const stopListening = useCallback(() => {
    if (recognitionRef.current && isListeningRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {}
    }
    isListeningRef.current = false;
    setIsListening(false);
  }, []);
  
  const sendMessageToAthena = async (message) => {
    if (!message.trim() || isProcessing) return;
    
    console.log('Sending message to Athena:', message);
    setIsProcessing(true);
    setTextInput('');
    
    try {
      const newHistory = [...conversationHistory, { role: 'user', content: message }];
      setConversationHistory(newHistory);
      
      const response = await api.athenaVoiceChat({
        message,
        voice_id: selectedVoice,
        conversation_history: conversationHistory
      });
      
      const { text, audio_url } = response.data;
      console.log('Athena response:', text);
      
      setAthenaResponse(text);
      setTranscript('');
      
      setConversationHistory(prev => [...prev, { role: 'assistant', content: text }]);
      
      if (audio_url) {
        setIsSpeaking(true);
        
        if (audioRef.current) {
          audioRef.current.src = audio_url;
          audioRef.current.playbackRate = 0.95;
          
          audioRef.current.onended = () => {
            console.log('Audio playback ended');
            setIsSpeaking(false);
            setIsProcessing(false);
            
            if (isCallActive && !isMuted && !useTextMode) {
              console.log('Restarting listening after Athena spoke');
              setTimeout(() => startListening(), 800);
            }
          };
          
          audioRef.current.onerror = (e) => {
            console.error('Audio playback error:', e);
            setIsSpeaking(false);
            setIsProcessing(false);
            
            if (isCallActive && !isMuted && !useTextMode) {
              setTimeout(() => startListening(), 500);
            }
          };
          
          audioRef.current.play().catch(e => {
            console.error('Audio play failed:', e);
            setIsSpeaking(false);
            setIsProcessing(false);
            
            if (isCallActive && !isMuted && !useTextMode) {
              setTimeout(() => startListening(), 500);
            }
          });
        }
      } else {
        setIsProcessing(false);
        if (isCallActive && !isMuted && !useTextMode) {
          setTimeout(() => startListening(), 1000);
        }
      }
      
    } catch (error) {
      console.error('Voice chat error:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to get response from Athena';
      toast.error(errorMessage);
      setIsProcessing(false);
      
      if (isCallActive && !isMuted && !useTextMode) {
        setTimeout(() => startListening(), 1000);
      }
    }
  };
  
  const handleTextSubmit = (e) => {
    e?.preventDefault();
    if (textInput.trim() && !isProcessing) {
      setTranscript(textInput);
      sendMessageToAthena(textInput);
    }
  };
  
  const startCall = async () => {
    if (!useTextMode) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach(track => track.stop());
      } catch (e) {
        toast.info('Microphone not available. Using text mode.');
        setUseTextMode(true);
      }
    }
    
    setIsCallActive(true);
    setConversationHistory([]);
    setAthenaResponse('');
    setTranscript('');
    
    toast.success('Connected to Athena!');
    
    setTimeout(() => {
      sendMessageToAthena("Hi Athena, I just started a call with you.");
    }, 500);
  };
  
  const endCall = () => {
    setIsCallActive(false);
    setIsListening(false);
    setIsSpeaking(false);
    setIsProcessing(false);
    
    stopListening();
    
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    
    toast.info('Call ended');
    if (onClose) onClose();
  };
  
  const toggleMute = () => {
    if (!isMuted) {
      stopListening();
      setIsMuted(true);
    } else {
      setIsMuted(false);
      if (isCallActive && !isProcessing && !isSpeaking && !useTextMode) {
        setTimeout(() => startListening(), 300);
      }
    }
  };
  
  const handlePushToTalk = () => {
    if (isListening) {
      stopListening();
    } else if (!isSpeaking && !isProcessing && !isMuted) {
      // Switch to voice mode and start listening
      setUseTextMode(false);
      startListening();
    }
  };
  
  const toggleTextMode = () => {
    setUseTextMode(!useTextMode);
    if (!useTextMode) {
      stopListening();
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
        onClick={(e) => e.target === e.currentTarget && !isCallActive && onClose?.()}
      >
        <motion.div 
          className="relative w-full max-w-md glass rounded-3xl overflow-hidden border border-amber-500/30 max-h-[90vh] overflow-y-auto"
          initial={{ y: 20 }}
          animate={{ y: 0 }}
        >
          <audio ref={audioRef} />
          
          {/* Header */}
          <div className="relative bg-gradient-to-r from-amber-900/50 to-black p-5">
            <button 
              onClick={() => !isCallActive && onClose?.()}
              className="absolute top-3 right-3 p-2 rounded-full hover:bg-white/10 transition-colors"
              disabled={isCallActive}
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
            
            <div className="flex items-center gap-4">
              <div className="relative">
                <img 
                  src="https://customer-assets.emergentagent.com/job_aipowered-5/artifacts/7tfm7q8x_ChatGPT%20Image%20Jan%209%2C%202026%2C%2005_02_53%20PM.png"
                  alt="Athena"
                  className="w-16 h-16 rounded-full object-cover border-2 border-amber-500/50"
                />
                {isCallActive && (
                  <div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-green-500 border-2 border-black animate-pulse" />
                )}
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">Athena</h2>
                <p className="text-amber-400 text-sm">AI Executive Assistant</p>
                {isCallActive && (
                  <p className="text-gray-400 text-xs mt-1">
                    {formatDuration(callDuration)} • {isSpeaking ? 'Speaking' : isListening ? 'Listening' : isProcessing ? 'Thinking' : 'Ready'}
                  </p>
                )}
              </div>
            </div>
          </div>
          
          {/* Main Content */}
          <div className="p-5 space-y-4">
            {!isCallActive ? (
              <>
                <p className="text-gray-300 text-center text-sm">
                  {isMobileDevice 
                    ? "Chat with Athena using voice or text. She'll respond with AI-powered voice."
                    : "Start a voice conversation with Athena. She'll listen and respond using AI-powered voice."
                  }
                </p>
                
                {/* Voice Selection */}
                <div className="space-y-2">
                  <button
                    onClick={() => setShowVoiceSettings(!showVoiceSettings)}
                    className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors mx-auto"
                  >
                    <Settings className="w-4 h-4" />
                    Choose Voice
                  </button>
                  
                  {showVoiceSettings && (
                    <motion.div 
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      className="grid grid-cols-2 gap-2"
                    >
                      {ATHENA_VOICES.map((voice) => (
                        <button
                          key={voice.id}
                          onClick={() => setSelectedVoice(voice.id)}
                          className={`p-2 rounded-xl text-left transition-all ${
                            selectedVoice === voice.id 
                              ? 'bg-amber-500/20 border border-amber-500/50' 
                              : 'bg-white/5 border border-transparent hover:bg-white/10'
                          }`}
                        >
                          <p className="text-white text-sm font-medium">{voice.name}</p>
                          <p className="text-gray-400 text-xs">{voice.description}</p>
                        </button>
                      ))}
                    </motion.div>
                  )}
                </div>
                
                {/* Start Call Button */}
                <Button
                  onClick={startCall}
                  className="w-full py-5 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-semibold text-base rounded-full"
                  data-testid="athena-start-call-btn"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Start Call with Athena
                </Button>
              </>
            ) : (
              <>
                {/* Active Call UI */}
                <div className="space-y-3">
                  {/* Voice Visualization */}
                  <div className="flex justify-center items-center h-16">
                    {isSpeaking ? (
                      <div className="flex items-center gap-1">
                        {[...Array(5)].map((_, i) => (
                          <motion.div
                            key={i}
                            className="w-1.5 bg-amber-500 rounded-full"
                            animate={{ height: [12, 32, 12] }}
                            transition={{ duration: 0.5, repeat: Infinity, delay: i * 0.1 }}
                          />
                        ))}
                        <span className="ml-2 text-amber-400 text-sm">Athena speaking...</span>
                      </div>
                    ) : isListening ? (
                      <div className="flex items-center gap-1">
                        {[...Array(5)].map((_, i) => (
                          <motion.div
                            key={i}
                            className="w-1.5 bg-cyan-500 rounded-full"
                            animate={{ height: [10, 24, 10] }}
                            transition={{ duration: 0.4, repeat: Infinity, delay: i * 0.08 }}
                          />
                        ))}
                        <span className="ml-2 text-cyan-400 text-sm">Listening...</span>
                      </div>
                    ) : isProcessing ? (
                      <div className="flex items-center gap-2">
                        <Loader className="w-5 h-5 text-amber-500 animate-spin" />
                        <span className="text-gray-400 text-sm">Athena thinking...</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                        <span className="text-gray-400 text-sm">Ready</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Transcript */}
                  {transcript && (
                    <div className="p-2 rounded-xl bg-cyan-500/10 border border-cyan-500/30">
                      <p className="text-xs text-cyan-400 mb-1">You:</p>
                      <p className="text-white text-sm">{transcript}</p>
                    </div>
                  )}
                  
                  {/* Athena Response */}
                  {athenaResponse && (
                    <div className="p-2 rounded-xl bg-amber-500/10 border border-amber-500/30 max-h-24 overflow-y-auto">
                      <p className="text-xs text-amber-400 mb-1">Athena:</p>
                      <p className="text-white text-sm">{athenaResponse}</p>
                    </div>
                  )}
                  
                  {/* Text Input (always visible on mobile, toggle on desktop) */}
                  {(useTextMode || isMobileDevice) && (
                    <form onSubmit={handleTextSubmit} className="flex gap-2">
                      <Input
                        type="text"
                        value={textInput}
                        onChange={(e) => setTextInput(e.target.value)}
                        placeholder="Type your message..."
                        className="flex-1 bg-black/30 border-white/20 text-white text-sm"
                        disabled={isProcessing || isSpeaking}
                      />
                      <Button 
                        type="submit"
                        className="bg-amber-500 hover:bg-amber-600 text-black px-3"
                        disabled={isProcessing || isSpeaking || !textInput.trim()}
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                    </form>
                  )}
                  
                  {/* Call Controls */}
                  <div className="flex justify-center items-center gap-3 pt-2">
                    {/* Mute Button */}
                    <button
                      onClick={toggleMute}
                      className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
                        isMuted 
                          ? 'bg-red-500/20 text-red-400' 
                          : 'bg-white/10 text-white hover:bg-white/20'
                      }`}
                      data-testid="athena-mute-btn"
                    >
                      {isMuted ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
                    </button>
                    
                    {/* Push to Talk Button - Always visible */}
                    {recognitionSupported && (
                      <button
                        onClick={handlePushToTalk}
                        disabled={isSpeaking || isProcessing || isMuted}
                        className={`w-14 h-14 rounded-full flex items-center justify-center transition-all ${
                          isListening 
                            ? 'bg-cyan-500 text-white scale-105 shadow-lg shadow-cyan-500/50' 
                            : 'bg-amber-500 text-black hover:bg-amber-600'
                        } ${(isSpeaking || isProcessing || isMuted) ? 'opacity-50' : ''}`}
                        data-testid="athena-push-to-talk-btn"
                      >
                        <Mic className="w-6 h-6" />
                      </button>
                    )}
                    
                    {/* End Call Button */}
                    <button
                      onClick={endCall}
                      className="w-12 h-12 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600 transition-colors"
                      data-testid="athena-end-call-btn"
                    >
                      <PhoneOff className="w-5 h-5" />
                    </button>
                  </div>
                  
                  {/* Help text */}
                  <p className="text-xs text-gray-500 text-center">
                    {useTextMode || isMobileDevice
                      ? 'Type your message or tap the mic button to speak'
                      : 'Tap the mic button to speak, or just talk after Athena responds'
                    }
                  </p>
                </div>
              </>
            )}
          </div>
          
          {/* Footer */}
          <div className="px-5 pb-4">
            <p className="text-xs text-gray-500 text-center">
              Powered by AI Horse-Powered Technology
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AthenaVoiceChat;
